import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button, Popover, Textarea } from '@wit-support/ui';
import { STATUS_LABEL_RU } from '@wit-support/ui-domain';
/**
 * Подтверждение смены статуса с НЕОБЯЗАТЕЛЬНЫМ комментарием.
 *
 * Popover, а не диалог: смена статуса — частое движение в очереди, и модальное
 * окно с оверлеем на каждый такой клик стоило бы дороже, чем приносит. Отсюда
 * же требование спеки «без комментария число кликов не хуже нынешнего»: поле
 * получает фокус при открытии, и один Enter подтверждает пустым — то есть
 * ровно та же пара движений (выбор → Enter), что была до появления поля.
 *
 * Shift+Enter НЕ подтверждает: комментарий бывает многострочным, и отнимать у
 * многострочного поля перенос строки — значит сделать его однострочным по
 * факту.
 *
 * Esc обрабатывается ЗДЕСЬ, а не только слоем Radix: под тестом Popover
 * подменяется прозрачной обёрткой без своего dismiss-слоя, и без явного
 * обработчика проверить отмену с клавиатуры было бы нечем. В браузере оба пути
 * ведут к одному и тому же `onCancel`, а он идемпотентен (сбрасывает уже
 * сброшенное намерение), так что двойной вызов ничего не портит.
 */
export function StatusChangePopover({ open, status, onConfirm, onCancel, pending = false, returnFocusTo, children, }) {
    const [comment, setComment] = React.useState('');
    const fieldRef = React.useRef(null);
    // Каждое открытие начинается с чистого поля: текст, набранный для ОТМЕНЁННОЙ
    // смены, не должен молча уехать вместе со следующей.
    React.useEffect(() => {
        if (open)
            setComment('');
    }, [open]);
    function confirm() {
        if (pending)
            return;
        const trimmed = comment.trim();
        onConfirm(trimmed.length > 0 ? trimmed : undefined);
    }
    return (_jsxs(Popover, { open: open, onOpenChange: (next) => {
            if (!next)
                onCancel();
        }, children: [_jsx(Popover.Anchor, { asChild: true, children: children }), open ? (_jsxs(Popover.Content, { align: "start", className: "flex w-72 flex-col gap-2", 
                // Фокус уходит в поле, а не на карточку: иначе первый Enter ничего не
                // подтверждает, а первая буква комментария улетает в никуда.
                onOpenAutoFocus: (event) => {
                    event.preventDefault();
                    fieldRef.current?.focus();
                }, 
                // После подтверждения (и отмены) клавиатура должна оказаться на
                // контроле статуса, а не на `body`: следующий Tab — к соседям
                // контрола, а не к началу страницы.
                onCloseAutoFocus: (event) => {
                    const target = returnFocusTo?.current;
                    if (!target)
                        return;
                    event.preventDefault();
                    target.focus();
                }, children: [_jsxs("p", { className: "text-sm font-medium text-[var(--color-text-primary)]", children: ["\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441 \u043D\u0430 \u00AB", STATUS_LABEL_RU[status], "\u00BB"] }), _jsx(Textarea, { ref: fieldRef, value: comment, onChange: (event) => setComment(event.target.value), "aria-label": "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439 (\u043D\u0435\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u043E)", placeholder: "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439 (\u043D\u0435\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u043E)", rows: 3, disabled: pending, className: "min-h-16 text-sm", onKeyDown: (event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                confirm();
                                return;
                            }
                            if (event.key === 'Escape')
                                onCancel();
                        } }), _jsxs("div", { className: "flex items-center justify-end gap-2", children: [_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: onCancel, disabled: pending, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { type: "button", size: "sm", onClick: confirm, disabled: pending, children: pending ? 'Смена…' : 'Сменить статус' })] })] })) : null] }));
}
//# sourceMappingURL=status-change-popover.js.map